import { LoadingButton } from '@mui/lab'
import { Box, Button, ButtonTypeMap, IconButton, Stack, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import './FloatingButtonBar.css'
import { useFloatingButtonBarVisibility } from 'hooks/use-floating-button-bar-visibility'
import { COLORS } from 'app-constants'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'

type Props = {
  onCancelClicked?: () => void
  updateLabel: string
  onUpdateClicked: () => void
  isUpdating: boolean
  isUpdateDisabled: boolean
  onTrashClicked?: () => void
  containerId?: string
  variant?: ButtonTypeMap['props']['variant']
  color?: ButtonTypeMap['props']['color']
}

// Adapted from https://stackoverflow.com/a/59154364
const FloatingButtonBar = ({
  onCancelClicked,
  updateLabel,
  onUpdateClicked,
  isUpdating,
  isUpdateDisabled,
  onTrashClicked,
  containerId,
  variant = 'contained',
  color = 'primary',
}: Props) => {
  const isMobile = useCheckMobileScreen()

  const [container] = useState(() => {
    // This will be executed only on the initial render
    // https://reactjs.org/docs/hooks-reference.html#lazy-initial-state
    return document.createElement('div')
  })
  const { setIsFloatingButtonBarOpen } = useFloatingButtonBarVisibility()

  useEffect(() => {
    let parent = containerId ? document.getElementById(containerId) : document.body

    container.classList.add('FloatingButtonBar')
    if (containerId) {
      container.classList.add('contained')
    }

    parent?.appendChild(container)
    setIsFloatingButtonBarOpen(true)

    return () => {
      parent?.removeChild(container)
      setIsFloatingButtonBarOpen(false)
    }
  }, [container, containerId, setIsFloatingButtonBarOpen])

  const renderStack = () => (
    <Stack
      direction="row"
      alignItems="center"
      height="100%"
      width="890px"
      padding="8px 8px 8px 12px"
      spacing={3}
      borderRadius="16px"
      mx={4}
      sx={{
        background: COLORS.JELLED_MID_BLUE_10_PERCENT,
        border: `1px solid ${COLORS.SPECIAL_STROKE}`,
        boxShadow: '4px 4px 20px 0px rgba(0, 0, 0, 0.08)',
      }}>
      {onTrashClicked && (
        <IconButton onClick={onTrashClicked}>
          <DeleteOutlinedIcon sx={{ fill: 'white' }} />
        </IconButton>
      )}
      {!isMobile && <Typography variant="h6">You have unsaved changes.</Typography>}
      <Box flex="1 1" />
      {onCancelClicked && (
        <Button
          variant="text"
          onClick={onCancelClicked}
          disabled={isUpdating}
          sx={{
            color: 'white',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}>
          Cancel
        </Button>
      )}
      <LoadingButton
        variant={variant}
        size="medium"
        color={color}
        onClick={onUpdateClicked}
        loading={isUpdating}
        disabled={isUpdateDisabled}>
        {updateLabel}
      </LoadingButton>
    </Stack>
  )

  return ReactDOM.createPortal(renderStack(), container)
}

FloatingButtonBar.defaultProps = {
  isUpdateDisabled: false,
}

export default FloatingButtonBar
